import { ReactElement } from 'react';
import { Box, Typography, FormControl, FormLabel, Alert } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { CabModal } from '@CabComponents/CabModal';
import { CabButton } from '@CabComponents/CabButton';
import { CabDatePicker } from '@CabComponents/CabDatePicker';
import { CabTimePicker } from '@CabComponents/CabTimePicker';
import { CabInput } from '@CabComponents/CabInput';
import { DateTime } from 'luxon';
import colors from '../../../../colors';
import { useConfirmationModalWithUI } from '../../../Common/ConfirmationModal';

export interface EditEventModalProps {
  open: boolean;
  onClose: () => void;
  eventTitle: string;
  currentStartTime: string;
  currentEndTime: string;
  onUpdateEvent: (title: string, startTime: string, endTime: string) => Promise<void>;
  onCancelEvent: (message: string) => Promise<void>;
}

interface EditEventFormData {
  title: string;
  selectedDate: string;
  startTime: string;
  endTime: string;
}

function hhmmToMinutes(t: string): number | null {
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

export const EditEventModal = ({
  open,
  onClose,
  eventTitle,
  currentStartTime,
  currentEndTime,
  onUpdateEvent,
  onCancelEvent
}: EditEventModalProps): ReactElement => {
  const { confirm: confirmCancel, ConfirmationModalComponent } = useConfirmationModalWithUI();
  
  // Parse current times
  const currentStart = DateTime.fromISO(currentStartTime);
  const currentEnd = DateTime.fromISO(currentEndTime);
  
  // Initialize form with current values
  const defaultValues: EditEventFormData = {
    title: eventTitle,
    selectedDate: currentStart.isValid ? currentStart.toISO() : '',
    startTime: `${currentStart.hour}:${currentStart.minute.toString().padStart(2, '0')}`,
    endTime: `${currentEnd.hour}:${currentEnd.minute.toString().padStart(2, '0')}`
  };

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    watch,
    reset,
    setValue
  } = useForm<EditEventFormData>({
    defaultValues,
    mode: 'onChange'
  });

  const [startTimeValue, endTimeValue] = watch(['startTime', 'endTime']);

  const startMins = hhmmToMinutes(startTimeValue);
  const endMins = hhmmToMinutes(endTimeValue);
  const durationMins = startMins != null && endMins != null && endMins > startMins ? endMins - startMins : 30;

  const handleUpdateEvent = async (data: EditEventFormData) => {
    try {
      const [startHour, startMinute] = data.startTime.split(':').map(Number);
      const [endHour, endMinute] = data.endTime.split(':').map(Number);
      
      const newStartDateTime = DateTime.fromISO(data.selectedDate)
        .set({ hour: startHour, minute: startMinute, second: 0, millisecond: 0 });
      const newEndDateTime = DateTime.fromISO(data.selectedDate)
        .set({ hour: endHour, minute: endMinute, second: 0, millisecond: 0 });
      
      await onUpdateEvent(
        data.title,
        newStartDateTime.isValid ? newStartDateTime.toISO() : '', 
        newEndDateTime.isValid ? newEndDateTime.toISO() : ''
      );
      handleClose();
    } catch (error) {
      console.error('Failed to update event:', error);
    }
  };

  const handleCancelEvent = async () => {
    const confirmed = await confirmCancel({
      title: "Cancel Event",
      message: "Are you sure you want to cancel this event?",
      confirmText: "Cancel Event",
      cancelText: "Keep Event",
      isDestructive: true,
    });
    
    if (confirmed) {
      try {
        await onCancelEvent('');
        handleClose();
      } catch (error) {
        console.error('Failed to cancel event:', error);
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Custom validation to ensure end time is after start time
  const validateEndTime = (endTime: string, formValues: EditEventFormData) => {
    const [startHour, startMinute] = formValues.startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
      return 'End time must be after start time';
    }
    return true;
  };

  return (
    <CabModal
      open={open}
      onClose={handleClose}
      title="Edit Event"
      closeIcon
      actionButtons={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            pt: 1
          }}>
          <CabButton
            buttonType="primary"
            color="error"
            onClick={handleCancelEvent}
            disabled={isSubmitting}
          >
            Cancel Event
          </CabButton>
          <CabButton
            onClick={handleSubmit(handleUpdateEvent)}
            disabled={isSubmitting || !isValid}
          >
            {isSubmitting ? 'Updating...' : 'Update Event'}
          </CabButton>
        </Box>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}>
          <FormControl>
            <FormLabel>Title</FormLabel>
            <Controller
              name="title"
              control={control}
              rules={{ required: 'Title is required' }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <CabInput
                    {...field}
                    placeholder="Enter event title"
                    size="small"
                  />
                  {error && (
                    <Alert severity="error" sx={{ marginTop: 1 }}>
                      {error.message}
                    </Alert>
                  )}
                </>
              )}
            />
          </FormControl>
        </Box>
        
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2
            }}>
            <FormControl>
              <FormLabel>Date</FormLabel>
              <Controller
                name="selectedDate"
                control={control}
                rules={{ required: 'Date is required' }}
                render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                  <>
                    <CabDatePicker
                      value={value}
                      onChange={(newDate) => onChange(newDate || '')}
                      size="small"
                    />
                    {error && (
                      <Alert severity="error" sx={{ marginTop: 1 }}>
                        {error.message}
                      </Alert>
                    )}
                  </>
                )}
              />
            </FormControl>
            
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center"
              }}>
              <Box sx={{
                flex: 1
              }}>
                <FormControl sx={{ width: '100%' }}>
                  <FormLabel>Start Time</FormLabel>
                  <Controller
                    name="startTime"
                    control={control}
                    rules={{ required: 'Start time is required' }}
                    render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                      <>
                        <CabTimePicker
                          pickerTime={field.value}
                          onChange={(newStartTime) => {
                            onChange(newStartTime);

                            const [h, m] = newStartTime.split(':').map(Number);
                            if (Number.isNaN(h) || Number.isNaN(m)) return;
                            const newStartMins = h * 60 + m;
                            const nextEndMins = Math.min(23 * 60 + 59, newStartMins + durationMins);
                            const eh = Math.floor(nextEndMins / 60);
                            const em = nextEndMins % 60;
                            setValue(
                              'endTime',
                              `${eh}:${em.toString().padStart(2, '0')}`,
                              { shouldValidate: true }
                            );
                          }}
                          maxTime={endTimeValue}
                          fullWidth
                        />
                        {error && (
                          <Alert severity="error" sx={{ marginTop: 1 }}>
                            {error.message}
                          </Alert>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Box>
              
              <Typography variant="h5" color={colors.black800} sx={{ marginTop: 3 }}>
                to
              </Typography>
              
              <Box sx={{
                flex: 1
              }}>
                <FormControl sx={{ width: '100%' }}>
                  <FormLabel>End Time</FormLabel>
                  <Controller
                    name="endTime"
                    control={control}
                    rules={{ 
                      required: 'End time is required',
                      validate: (value, formValues) => validateEndTime(value, formValues)
                    }}
                    render={({ field: { ref, ...field }, fieldState: { error } }) => (
                      <>
                        <CabTimePicker
                          pickerTime={field.value}
                          onChange={field.onChange}
                          minTime={startTimeValue}
                          fullWidth
                        />
                        {error && (
                          <Alert severity="error" sx={{ marginTop: 1 }}>
                            {error.message}
                          </Alert>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <ConfirmationModalComponent />
    </CabModal>
  );
};

export default EditEventModal;
